import { z } from 'zod';

import { todayLocal } from '../utils/datetime';

/**
 * Tipos de cobrança suportados pelo Control Center.
 *
 * - `recorrente`: gerada automaticamente pelo cron a partir de uma subscription
 *   (story 4.3). Tem `subscriptionId` preenchido.
 * - Demais tipos: avulsas manuais (story 4.4). Sem `subscriptionId`.
 *
 * `acordo_quitacao` é o único tipo que pode ser emitido pra cliente `cancelado`
 * (DR-022/3.4 — exceção pra acordos de quitação de débito).
 */
export const chargeTypeSchema = z.enum([
  'recorrente',
  'implantacao',
  'consultoria_extra',
  'treinamento',
  'desenvolvimento_personalizado',
  'taxa_setup',
  'proration_upgrade',
  'acordo_quitacao',
  'outro',
]);
export type ChargeType = z.infer<typeof chargeTypeSchema>;

/**
 * Tipos de cobrança manual avulsa. `proration_upgrade` só nasce do fluxo de
 * upgrade de plano — não pode ser criada à mão.
 */
export const manualChargeTypeSchema = chargeTypeSchema.exclude([
  'recorrente',
  'proration_upgrade',
]);
export type ManualChargeType = z.infer<typeof manualChargeTypeSchema>;

/**
 * Schema Zod de status — usa a união de tipos existente em `../types`
 * (que cobre todo o ciclo conceitual). Validação de runtime; valores
 * válidos no DB são restritos pelo enum Prisma.
 *
 * NÃO exportamos o tipo daqui pra evitar conflito com `ChargeStatus` em
 * `../types` — esse tipo continua sendo a fonte de verdade.
 */
export const chargeStatusEnumSchema = z.string();

/**
 * Métodos de pagamento aceitos no formulário de avulsa.
 * Mapeados pra `billingType` do Asaas no service.
 */
/**
 * Formas aceitas numa cobrança. `boleto` saiu em 2026-08-19: não combina com
 * assinatura (não debita sozinho, atrasa conciliação) e foi proibido pelo dono.
 * O enum do Prisma ainda tem `boleto` de propósito — remover valor de enum é
 * migration destrutiva, e barrar aqui, na ENTRADA, tem o mesmo efeito.
 */
export const chargePaymentMethodSchema = z.enum(['pix', 'cartao']);
export type ChargePaymentMethod = z.infer<typeof chargePaymentMethodSchema>;

/**
 * Body do `POST /api/charges` — criação de cobrança avulsa.
 *
 * Validações:
 * - amount: positivo, máx R$ 999.999,99 (precision do schema)
 * - description: 1-200 chars
 * - dueDate: YYYY-MM-DD (string ISO date), >= hoje
 * - type: qualquer manual (não pode ser `recorrente`)
 *
 * Bloqueios de status do cliente são aplicados no service, não no schema
 * (precisa consultar o DB).
 */
export const createManualChargeSchema = z.object({
  clientId: z.string().uuid(),
  type: manualChargeTypeSchema,
  amount: z
    .number()
    .positive('Valor deve ser maior que zero')
    .max(999_999.99, 'Valor excede limite máximo')
    .multipleOf(0.01, 'Valor deve ter no máximo 2 casas decimais'),
  description: z
    .string()
    .trim()
    .min(1, 'Descrição obrigatória')
    .max(200, 'Descrição deve ter no máximo 200 caracteres'),
  dueDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data deve estar no formato YYYY-MM-DD')
    .refine((v) => v >= todayLocal(), 'Vencimento deve ser hoje ou no futuro'),
  // Story 4.14 (D1): o método é escolhido pelo CLIENTE no checkout. Opcional na
  // criação — a charge nasce sem método (status aguardando_pagamento). Mantido
  // opcional pra transição enquanto o form admin (feat-4) ainda não foi ajustado.
  paymentMethod: chargePaymentMethodSchema.optional(),
});
export type CreateManualChargeRequest = z.infer<typeof createManualChargeSchema>;

/**
 * Query params do `GET /api/charges`.
 * Filtros pra listagem (4.4 + 4.10).
 *
 * Story 4.10 adicionou: status, dueFrom/dueTo, clientId, subscriptionId.
 */
export const listChargesQuerySchema = z.object({
  /** 'recorrentes' filtra `type=recorrente`; 'avulsas' filtra `type != recorrente`. */
  scope: z.enum(['todas', 'recorrentes', 'avulsas']).optional().default('todas'),
  /** Filtro por status — match exato. */
  status: chargeStatusEnumSchema.optional(),
  /** Range de due_date — formato YYYY-MM-DD. */
  dueFrom: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato YYYY-MM-DD')
    .optional(),
  dueTo: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato YYYY-MM-DD')
    .optional(),
  clientId: z.string().uuid().optional(),
  subscriptionId: z.string().uuid().optional(),
  /** Página 1-based. */
  page: z.coerce.number().int().positive().optional().default(1),
  /** Itens por página, 1-100. */
  pageSize: z.coerce.number().int().min(1).max(100).optional().default(20),
});
export type ListChargesQuery = z.infer<typeof listChargesQuerySchema>;

/**
 * Item da listagem de charges (shape do response).
 * Mantemos amount/effectivePrice como string pra preservar precisão (Decimal).
 */
export const chargeListItemSchema = z.object({
  id: z.string().uuid(),
  subscriptionId: z.string().uuid().nullable(),
  clientId: z.string().uuid(),
  clientName: z.string(),
  type: chargeTypeSchema,
  amount: z.string(),
  effectivePrice: z.string().nullable(),
  description: z.string(),
  dueDate: z.string(),
  competence: z.string(),
  status: chargeStatusEnumSchema,
  asaasPaymentId: z.string().nullable(),
  paymentMethod: z.string().nullable(),
  paymentLink: z.string().nullable(),
  createdAt: z.string(),
});
export type ChargeListItem = z.infer<typeof chargeListItemSchema>;

export const listChargesResponseSchema = z.object({
  data: z.array(chargeListItemSchema),
  pagination: z.object({
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    total: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
  }),
});
export type ListChargesResponse = z.infer<typeof listChargesResponseSchema>;

export const createManualChargeResponseSchema = z.object({
  data: chargeListItemSchema,
});
export type CreateManualChargeResponse = z.infer<typeof createManualChargeResponseSchema>;

// ============================================================================
// Story 4.7 — Pagamento manual fora do Asaas (PIX direto, TED, depósito)
// ============================================================================

/**
 * Métodos de pagamento manual aceitos.
 * Operador escolhe quando marca charge como paga sem passar pelo Asaas.
 */
export const manualPaymentMethodSchema = z.enum([
  'pix_manual',
  'ted',
  'dinheiro',
  'deposito',
  'outro',
]);
export type ManualPaymentMethod = z.infer<typeof manualPaymentMethodSchema>;

/**
 * Body do `POST /api/charges/:id/mark-paid-manual`.
 *
 * `externalReference` obrigatório — operador precisa registrar qual foi a referência
 * do pagamento (ID PIX, número TED, código do depósito, comprovante). Auditoria fiscal.
 *
 * `attachmentUrl` opcional — URL externa do anexo (S3 manual, Drive, Dropbox).
 * Story 4.7 não implementa upload nativo (escopo futuro).
 *
 * `paidAt` no formato ISO 8601 — operador escolhe a data real do pagamento.
 */
export const markPaidManualSchema = z.object({
  amount: z
    .number()
    .positive('Valor deve ser maior que zero')
    .max(999_999.99, 'Valor excede limite máximo')
    .multipleOf(0.01, 'Valor deve ter no máximo 2 casas decimais'),
  paidAt: z
    .string()
    .datetime({ message: 'Data deve estar no formato ISO 8601 (ex: 2026-06-02T14:30:00Z)' }),
  method: manualPaymentMethodSchema,
  externalReference: z
    .string()
    .trim()
    .min(1, 'Referência obrigatória (ID PIX, número TED, comprovante)')
    .max(200, 'Referência deve ter no máximo 200 caracteres'),
  attachmentUrl: z.string().url('URL inválida').max(1000, 'URL muito longa').optional(),
  notes: z.string().trim().max(500, 'Observações devem ter no máximo 500 caracteres').optional(),
});
export type MarkPaidManualRequest = z.infer<typeof markPaidManualSchema>;

export const markPaidManualResponseSchema = z.object({
  data: z.object({
    chargeId: z.string().uuid(),
    paymentId: z.string().uuid(),
    status: z.string(),
    paidAt: z.string(),
  }),
});
export type MarkPaidManualResponse = z.infer<typeof markPaidManualResponseSchema>;

// ============================================================================
// Story 4.10 — Detalhe de charge (com payments + timeline de audit log)
// ============================================================================

/** Item de payment associado à charge. */
export const chargePaymentItemSchema = z.object({
  id: z.string().uuid(),
  amount: z.string(),
  method: z.string().nullable(),
  paidAt: z.string().nullable(),
  status: z.string(),
  externalReference: z.string().nullable(),
  asaasPaymentId: z.string().nullable(),
  createdAt: z.string(),
});
export type ChargePaymentItem = z.infer<typeof chargePaymentItemSchema>;

/** Entrada de timeline (audit log) — ordem reversa cronológica. */
export const chargeTimelineEntrySchema = z.object({
  id: z.string().uuid(),
  action: z.string(),
  source: z.string().nullable(),
  userName: z.string().nullable(),
  createdAt: z.string(),
  /** Shape varia conforme source — frontend exibe campos conhecidos. */
  oldValue: z.unknown().nullable(),
  newValue: z.unknown().nullable(),
});
export type ChargeTimelineEntry = z.infer<typeof chargeTimelineEntrySchema>;

/** Item de refund associado à charge (story 4.11). */
export const chargeRefundItemSchema = z.object({
  id: z.string().uuid(),
  amount: z.string(),
  type: z.enum(['manual', 'asaas_full', 'asaas_partial']),
  reason: z.string().nullable(),
  externalReference: z.string().nullable(),
  attachmentUrl: z.string().nullable(),
  byUserName: z.string().nullable(),
  refundedAt: z.string(),
  createdAt: z.string(),
});
export type ChargeRefundItem = z.infer<typeof chargeRefundItemSchema>;

export const chargeDetailSchema = chargeListItemSchema.extend({
  bankSlipUrl: z.string().nullable(),
  pixCopyPaste: z.string().nullable(),
  errorMessage: z.string().nullable(),
  attemptsCount: z.number().int().nonnegative(),
  subscriptionId: z.string().uuid().nullable(),
  payments: z.array(chargePaymentItemSchema),
  timeline: z.array(chargeTimelineEntrySchema),
  /** Story 4.11 — lista de refunds + saldo restante. */
  refunds: z.array(chargeRefundItemSchema),
  refundedTotal: z.string(),
  remainingBalance: z.string(),
});
export type ChargeDetail = z.infer<typeof chargeDetailSchema>;

export const chargeDetailResponseSchema = z.object({
  data: chargeDetailSchema,
});
export type ChargeDetailResponse = z.infer<typeof chargeDetailResponseSchema>;

// ============================================================================
// Story 4.8 — Atualizar data de vencimento (boleto vencido)
// ============================================================================

/**
 * Body do `POST /api/charges/:id/update-due-date`.
 *
 * `newDueDate` no formato YYYY-MM-DD, OBRIGATÓRIO > hoje (Zod refine).
 * Asaas mantém mesmo payment_id + juros/multa acumulados (AC2).
 *
 * `sendNotification` default=true — quando false, operador atualiza data
 * sem reenviar link (caso já tenha avisado por outro canal).
 */
export const updateDueDateSchema = z.object({
  newDueDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data deve estar no formato YYYY-MM-DD')
    .refine((v) => v > todayLocal(), 'Nova data deve ser depois de hoje'),
  sendNotification: z.boolean().optional().default(true),
});
export type UpdateDueDateRequest = z.infer<typeof updateDueDateSchema>;

export const updateDueDateResponseSchema = z.object({
  data: z.object({
    chargeId: z.string().uuid(),
    newDueDate: z.string(),
    status: z.string(),
    notificationEnqueued: z.boolean(),
  }),
});
export type UpdateDueDateResponse = z.infer<typeof updateDueDateResponseSchema>;
